'use client';

import { useEffect, useRef } from 'react';

interface SectionEngagement {
  sectionId: string;
  timeSpent: number;
  scrollDepth: number;
  interactions: number;
}

interface ConversionTrackerProps {
  sections: string[];
  onEngagementUpdate?: (engagement: SectionEngagement[]) => void;
  trackingInterval?: number;
}

const ConversionTracker = ({ 
  sections, 
  onEngagementUpdate,
  trackingInterval = 5000 
}: ConversionTrackerProps) => {
  const engagementData = useRef<Map<string, SectionEngagement>>(new Map());
  const currentSection = useRef<string>('');
  const sectionStartTime = useRef<number>(Date.now());
  const maxScrollDepth = useRef<number>(0);

  useEffect(() => {
    sections.forEach(sectionId => {
      if (!engagementData.current.has(sectionId)) {
        engagementData.current.set(sectionId, {
          sectionId,
          timeSpent: 0,
          scrollDepth: 0,
          interactions: 0
        });
      }
    });
  }, [sections]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 2;
      const documentHeight = document.documentElement.scrollHeight;
      const currentScrollDepth = (window.scrollY + window.innerHeight) / documentHeight * 100;
      
      maxScrollDepth.current = Math.max(maxScrollDepth.current, currentScrollDepth);

      let newSection = '';
      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element && element.offsetTop <= scrollPosition) {
          newSection = sectionId;
        }
      }

      if (newSection && newSection !== currentSection.current) {
        if (currentSection.current) {
          const timeSpent = Date.now() - sectionStartTime.current;
          const engagement = engagementData.current.get(currentSection.current);
          if (engagement) {
            engagement.timeSpent += timeSpent;
            engagement.scrollDepth = Math.max(engagement.scrollDepth, maxScrollDepth.current);
          }
        }

        currentSection.current = newSection;
        sectionStartTime.current = Date.now();
        maxScrollDepth.current = 0;
      }
    };

    const handleInteraction = (event: Event) => {
      if (currentSection.current) {
        const engagement = engagementData.current.get(currentSection.current);
        if (engagement) {
          engagement.interactions += 1;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('click', handleInteraction);
    document.addEventListener('submit', handleInteraction);

    handleScroll();

    const interval = setInterval(() => {
      if (currentSection.current) {
        const timeSpent = Date.now() - sectionStartTime.current;
        const engagement = engagementData.current.get(currentSection.current);
        if (engagement) {
          engagement.timeSpent += timeSpent;
          engagement.scrollDepth = Math.max(engagement.scrollDepth, maxScrollDepth.current);
        }
        sectionStartTime.current = Date.now();
      }

      if (onEngagementUpdate) {
        onEngagementUpdate(Array.from(engagementData.current.values()));
      }
    }, trackingInterval);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('submit', handleInteraction);
      clearInterval(interval);
    };
  }, [sections, onEngagementUpdate, trackingInterval]);

  return null;
};

export default ConversionTracker;